/**
 * The UI-agent task shape, held still across vendors.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * These types have outlived two providers. They were shaped to Manus's
 * UI_AGENT_RESULT_SCHEMA, then carried unchanged through Steel, and now through
 * Browser Use Cloud. Each time, holding the interface still is what let call
 * sites move one at a time instead of in a single irreversible commit.
 *
 * They used to live in steel-agent.ts, so browser-use-agent.ts had to import
 * from the module it was replacing. Deleting that module would have taken the
 * types with it. They live here now, owned by no provider.
 *
 * THE NAMES ARE THE CONTRACT
 * --------------------------
 * `Steel` in these names is now only history, and it stays anyway. The field
 * names are read by things this package cannot type-check:
 *
 *   - `steel_task:<id>` KV records written by one deploy and read by the next,
 *     so a record written before a rename is still out there for seven days.
 *   - apps/console reads `steel_session_id`, `pending_question`,
 *     `session_viewer_url` and the `steel_task:` prefix over the KV REST API,
 *     with no shared types between the two apps.
 *   - `ui_agent_status` returns these fields straight to MCP clients.
 *
 * A rename here compiles clean and breaks all three silently. Rename them in
 * the same change that updates the console and drains the KV records, or not at
 * all.
 */

/** Structured result of a finished run. Matches Manus UI_AGENT_RESULT_SCHEMA. */
export interface SteelTaskResult {
  success: boolean;
  summary: string;
  entity_id?: string;
  error_message?: string;
  screenshots_taken: number;
}

/** The four states every downstream reader understands. */
export type SteelTaskStatus = "running" | "stopped" | "waiting" | "error";

/** Full task record stored in KV under `steel_task:<id>` and returned from dispatch. */
export interface SteelTask {
  task_id: string;
  status: SteelTaskStatus;
  /** When set, only the same Carrier OAuth `sub` may read status via `ui_agent_status`. */
  owner_sub?: string;
  /** Provider session id. Rendered by the console's task detail view. */
  steel_session_id?: string;
  /** Provider run id, when the provider assigns one separately from the session. */
  run_id?: string;
  session_viewer_url?: string;
  title?: string;
  created_at: string;
  updated_at: string;
  result?: SteelTaskResult;
  pending_question?: string;
}

// ---------------------------------------------------------------------------
// Pending-ask KV schema
// ---------------------------------------------------------------------------

/**
 * KV key prefix for pending-ask entries.
 *
 * Still `manus_pending_ask:` two providers later, for the same reason as
 * everything else here: live entries carry a 24h TTL, so a prefix change strands
 * every ask in flight at deploy time and `ui_agent_list_pending` reports none.
 */
export const PENDING_ASK_PREFIX = "manus_pending_ask:";
export const PENDING_ASK_TTL_SECONDS = 24 * 3600;

export interface SteelPendingAsk {
  task_id: string;
  question: string;
  task_url: string;
  asked_at: string; // ISO 8601
}
